import { arc } from '@visx/shape';

export const RING_RADIUS_FACTOR = 0.05;
export const SELECTED_RADIUS_FACTOR = 0.03;
export const MIN_HIGHLIGHT_ANGLE = (1.5 * Math.PI) / 180;
export const DONUT_INNER_RADIUS_RATIO = 0.75;
export const STROKE_WIDTH = 1;

export type ArcAngles = { startAngle: number; endAngle: number; padAngle: number };

export const computeArcPath = (angles: ArcAngles, baseRadius: number, selected: boolean, innerRadius = 0) => {
  const outerRadius = selected ? baseRadius * (1 + SELECTED_RADIUS_FACTOR) : baseRadius;
  return (
    arc<ArcAngles>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .startAngle(d => d.startAngle)
      .endAngle(d => d.endAngle)
      .padAngle(d => d.padAngle)(angles) ?? undefined
  );
};

export const computeRingArc = (angles: ArcAngles, baseRadius: number, selected: boolean) => {
  const effectiveRadius = selected ? baseRadius * (1 + SELECTED_RADIUS_FACTOR) : baseRadius;
  const ringWidth = baseRadius * RING_RADIUS_FACTOR;
  const ringOuterRadius = effectiveRadius + ringWidth;
  const ringInnerRadius = effectiveRadius;
  const ringArc = arc()
    .innerRadius(ringInnerRadius)
    .outerRadius(ringOuterRadius)
    .startAngle(angles.startAngle)
    .endAngle(angles.endAngle)
    .padAngle(angles.padAngle)(null);

  return { ringOuterRadius, ringInnerRadius, ringArc };
};

const isSmallSlice = (a: ArcAngles) => a.endAngle - a.startAngle < MIN_HIGHLIGHT_ANGLE;

export const adjustArcsForHighlight = (arcs: ArcAngles[], highlightedIndex: number | null): ArcAngles[] => {
  const noHighlight = highlightedIndex === null || highlightedIndex < 0 || highlightedIndex >= arcs.length;
  if (noHighlight) return arcs;

  if (!isSmallSlice(arcs[highlightedIndex])) return arcs;

  return expandSmallArcs(arcs, new Set([highlightedIndex]));
};

export const adjustArcsForSelection = (arcs: ArcAngles[], selectedIndices: Set<number>): ArcAngles[] => {
  if (selectedIndices.size === 0) return arcs;

  const expandSet = new Set<number>();
  selectedIndices.forEach(i => {
    if (i >= 0 && i < arcs.length && isSmallSlice(arcs[i])) expandSet.add(i);
  });

  return expandSmallArcs(arcs, expandSet);
};

const expandSmallArcs = (arcs: ArcAngles[], expandSet: Set<number>): ArcAngles[] => {
  if (expandSet.size === 0) return arcs;

  const { totalDeficit, totalOtherSpan } = arcs.reduce(
    (acc, a, i) => {
      const span = a.endAngle - a.startAngle;
      return expandSet.has(i)
        ? { ...acc, totalDeficit: acc.totalDeficit + MIN_HIGHLIGHT_ANGLE - span }
        : { ...acc, totalOtherSpan: acc.totalOtherSpan + span };
    },
    { totalDeficit: 0, totalOtherSpan: 0 }
  );

  if (totalOtherSpan <= 0) return arcs;

  const { result } = arcs.reduce<{ cursor: number; result: ArcAngles[] }>(
    (acc, a, i) => {
      const originalSpan = a.endAngle - a.startAngle;
      const adjustedSpan = expandSet.has(i) ? MIN_HIGHLIGHT_ANGLE : originalSpan - (totalDeficit * originalSpan) / totalOtherSpan;
      return {
        cursor: acc.cursor + adjustedSpan,
        result: [...acc.result, { startAngle: acc.cursor, endAngle: acc.cursor + adjustedSpan, padAngle: a.padAngle }],
      };
    },
    { cursor: arcs[0].startAngle, result: [] }
  );

  return result;
};
